import React, { createContext, useContext } from 'react';
import type {
  AnalyticsFilters,
  AnalyticsSummary,
  CapitalHistoryResponse,
  DailyPnlPoint,
} from '../types/analytics';
import type { BrokerAccount } from '../types';

/**
 * Shared analytics data — fetched once by AnalyticsLayout, consumed by every
 * section route. Keeps filter changes from re-triggering identical fetches
 * across sub-pages. Section-local data (top trades, breakdowns, positions
 * panel, etc.) stays inside each section component.
 */
export interface AnalyticsDataValue {
  filters: AnalyticsFilters;
  setFilters: (f: AnalyticsFilters) => void;

  summary: AnalyticsSummary | null;
  capital: CapitalHistoryResponse | null;
  dailyPoints: DailyPnlPoint[];

  accounts: Array<{ broker: string; clientId: string; label: string }>;
  /** Full broker-account rows — needed by capital-edit surfaces that can't
   *  work from the slim {broker, clientId, label} projection. */
  brokerAccounts: BrokerAccount[];
  baskets: Array<{ id: string; name: string }>;
  strategies: Array<{ id: string; name: string }>;
  /** Re-fetch broker accounts. Call after capital edits so the analytics
   *  data + the prompt's dismiss-state both update without a full reload. */
  refetchBrokerAccounts: () => void;

  loading: boolean;
  error: string | null;

  refetch: () => void;
}

const AnalyticsDataContext = createContext<AnalyticsDataValue | null>(null);

export const AnalyticsDataProvider: React.FC<{
  value: AnalyticsDataValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <AnalyticsDataContext.Provider value={value}>{children}</AnalyticsDataContext.Provider>
);

export function useAnalyticsData(): AnalyticsDataValue {
  const v = useContext(AnalyticsDataContext);
  if (!v) {
    throw new Error('useAnalyticsData must be used inside <AnalyticsDataProvider>');
  }
  return v;
}

/** Same as useAnalyticsData but returns null instead of throwing when no
 *  provider is mounted. For components that can be rendered either inside
 *  AnalyticsLayout (context available) or standalone (filters via prop). */
export function useAnalyticsDataOptional(): AnalyticsDataValue | null {
  return useContext(AnalyticsDataContext);
}
